/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable no-param-reassign */
import SettingsIcon from "@mui/icons-material/Settings";
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import * as Cesium from "cesium";
import React from "react";
import { useTranslation } from "react-i18next";
import { isNumber } from "~/utils/isNumber";
import { SavedElevationSettings } from "../../functions/colorAdjustments";
import { CustomTerrainProvider } from "../../types/CesiumMapItems";
import { ColorAdjustmentDialog } from "./ColorAdjustmentDialog";
import { ShadingMode, ShadingModes } from "./TerrainAdjustmenDialog";

type Props = {
  viewer: Cesium.Viewer;
  onSettingsChange: (val: string) => void;
  shadingMode: ShadingMode;
  onShadingModeChange: (mode: ShadingMode) => void;
};

export const terrainCanvasId = "terrain-coloring-canvas";

export const TerrainColorAdjustment = ({
  viewer,
  onSettingsChange,
  shadingMode,
  onShadingModeChange,
}: Props): JSX.Element | null => {
  const { t } = useTranslation("cesium");
  const [colorAdjustmentDialogOpen, setColorAdjustmentDialogOpen] =
    React.useState(false);
  const [elevationSettings, setElevationSettings] =
    React.useState<SavedElevationSettings>();

  const terrainActualMinHeight = React.useMemo(
    () => (viewer.terrainProvider as CustomTerrainProvider).minHeight,
    [viewer.terrainProvider],
  );
  const terrainActualMaxHeight = React.useMemo(
    () => (viewer.terrainProvider as CustomTerrainProvider).maxHeight,
    [viewer.terrainProvider],
  );

  const initialMinHeight = React.useMemo(
    () =>
      (viewer.terrainProvider as CustomTerrainProvider).elevationSettings
        ?.minZ ?? terrainActualMinHeight,
    [terrainActualMinHeight, viewer.terrainProvider],
  );
  const initialMaxHeight = React.useMemo(
    () =>
      (viewer.terrainProvider as CustomTerrainProvider).elevationSettings
        ?.maxZ ?? terrainActualMaxHeight,
    [terrainActualMaxHeight, viewer.terrainProvider],
  );
  const initialValues = React.useMemo(
    () => (viewer.terrainProvider as CustomTerrainProvider).elevationSettings,
    [viewer.terrainProvider],
  );

  const getColorRamp = React.useCallback(() => {
    const ramp =
      (document.getElementById(terrainCanvasId) as HTMLCanvasElement) ??
      document.createElement("canvas");
    ramp.setAttribute("id", terrainCanvasId);
    const width = 100;
    const height = 1;
    ramp.width = width;
    ramp.height = height;
    const ctx = ramp.getContext("2d");
    if (!ctx) return ramp;
    if (!elevationSettings?.colorsAndSteps) return ramp;
    const grd = ctx.createLinearGradient(0, 0, 100, 0);
    elevationSettings?.colorsAndSteps.forEach((c) => {
      if (isNumber(c.stepMin) && c.stepMin !== Number.NEGATIVE_INFINITY)
        grd.addColorStop(c.stepMin / 100, c.color.toString());
    });

    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, width, height);

    return ramp;
  }, [elevationSettings]);

  React.useEffect(() => {
    let material: Cesium.Material | undefined;
    const minHeight = elevationSettings?.minZ ?? terrainActualMinHeight;
    const maxHeight = elevationSettings?.maxZ ?? terrainActualMaxHeight;
    switch (shadingMode) {
      case "elevation":
        material = Cesium.Material.fromType("ElevationRamp");
        material.uniforms.minimumHeight = minHeight;
        material.uniforms.maximumHeight = maxHeight;
        break;
      case "no-shading":
        material = undefined;
        break;

      default:
        break;
    }
    if (material) material.uniforms.image = getColorRamp();
    viewer.scene.globe.material = material as Cesium.Material; // Assign as undefined when resetting material
  }, [
    shadingMode,
    terrainActualMinHeight,
    terrainActualMaxHeight,
    getColorRamp,
    viewer.scene.globe,
    viewer.terrainProvider,
    elevationSettings,
  ]);

  const handleShadingModeChange = React.useCallback(
    (event: SelectChangeEvent<"elevation" | "no-shading">) => {
      const val = event.target.value as ShadingMode;
      onShadingModeChange(val);
    },
    [onShadingModeChange],
  );

  const handleColorAdjustmentDialogClose = React.useCallback(
    () => setColorAdjustmentDialogOpen(false),
    [],
  );

  const handleColorAdjustmentDialogOpen = React.useCallback(
    () => setColorAdjustmentDialogOpen(true),
    [],
  );
  const handleElevationSettingsChange = React.useCallback(
    (settings: string) => {
      setElevationSettings(JSON.parse(settings) as SavedElevationSettings);
      onSettingsChange(settings);
    },
    [onSettingsChange],
  );

  return (
    <>
      <Grid container justifyContent="center" alignItems="center">
        <Grid item xs={5}>
          <FormControl fullWidth>
            <InputLabel>{t("terrain-shading-mode")}</InputLabel>
            <Select value={shadingMode} onChange={handleShadingModeChange}>
              {ShadingModes.map((c) => (
                <MenuItem value={c} key={c}>
                  {t(`cesium-shading-modes:${c}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {shadingMode === "elevation" ? (
          <Grid item xs={2} container>
            <IconButton
              sx={{ ml: 1 }}
              onClick={handleColorAdjustmentDialogOpen}
            >
              <SettingsIcon />
            </IconButton>
          </Grid>
        ) : null}
      </Grid>
      <ColorAdjustmentDialog
        open={colorAdjustmentDialogOpen}
        onClose={handleColorAdjustmentDialogClose}
        onElevationStringChange={handleElevationSettingsChange}
        colorCondition="elevation"
        tilesetMinValAbs={terrainActualMinHeight}
        tilesetMaxValAbs={terrainActualMaxHeight}
        initialMinValAbs={initialMinHeight}
        initialMaxValAbs={initialMaxHeight}
        initialColoringBlendingMode={initialValues?.blendingMode}
        initialColoringValueMode={initialValues?.valueMode}
        initialColorsAndSteps={initialValues?.colorsAndSteps}
      />
    </>
  );
};
